/**
 * Lightweight page tracking hook.
 * Sends time-on-page data via sendBeacon when the user leaves.
 * No visible network requests in devtools (sendBeacon fires in background).
 *
 * Used by:
 * - PublicLayout.tsx to track all public page visits
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE = '';

/**
 * Get or create a persistent visitor ID.
 * Stored in localStorage and set as a cookie so it's sent with every request
 * (including the initial essay fetch that triggers server-side tracking).
 */
export function getOrCreateVisitorId(): string {
  const KEY = '_vid';
  try {
    let vid = localStorage.getItem(KEY);
    if (!vid) {
      vid = crypto.randomUUID();
      localStorage.setItem(KEY, vid);
    }
    // Set/refresh cookie so it's sent with every API request
    document.cookie = `_vid=${vid}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Strict`;
    return vid;
  } catch {
    // localStorage unavailable (e.g., some privacy modes)
    return crypto.randomUUID();
  }
}

// Set cookie immediately on module load, before any API fetch fires
const visitorId = getOrCreateVisitorId();

// Module-level video progress that any component can report to.
// Reset by usePageTracking on each navigation.
let _maxVideoProgress = 0;
export function reportVideoProgress(pct: number) {
  if (pct > _maxVideoProgress) _maxVideoProgress = pct;
}

export function usePageTracking() {
  const location = useLocation();
  const startTime = useRef(Date.now());
  const currentPath = useRef(location.pathname);
  const maxScrollDepth = useRef(0);
  const lastSentScrollDepth = useRef(0);
  const lastSentVideoProgress = useRef(0);

  useEffect(() => {
    // Reset state on navigation
    startTime.current = Date.now();
    currentPath.current = location.pathname;
    maxScrollDepth.current = 0;
    _maxVideoProgress = 0;
    lastSentScrollDepth.current = 0;
    lastSentVideoProgress.current = 0;

    function trackScroll() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (docHeight > 0) {
        const pct = Math.round((scrollTop / docHeight) * 100);
        if (pct > maxScrollDepth.current) {
          maxScrollDepth.current = pct;
        }
      }
    }

    function sendBeacon() {
      const duration = Math.round((Date.now() - startTime.current) / 1000);
      if (duration < 1) return;

      const match = currentPath.current.match(/^\/essays\/([^/]+)$/);
      const payload = JSON.stringify({
        p: currentPath.current,
        d: duration,
        s: match ? match[1] : undefined,
        vid: visitorId,
        sd: maxScrollDepth.current > 0 ? maxScrollDepth.current : undefined,
        vp: _maxVideoProgress > 0 ? _maxVideoProgress : undefined,
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          `${API_BASE}/api/public/v`,
          new Blob([payload], { type: 'application/json' })
        );
      }
      lastSentScrollDepth.current = maxScrollDepth.current;
      lastSentVideoProgress.current = _maxVideoProgress;
    }

    window.addEventListener('scroll', trackScroll, { passive: true });

    // Periodic beacon every 10s so scroll/video/duration updates aren't lost
    const intervalId = setInterval(() => {
      const scrollAdvanced = maxScrollDepth.current > lastSentScrollDepth.current;
      const videoAdvanced = _maxVideoProgress > lastSentVideoProgress.current;
      if (scrollAdvanced || videoAdvanced) {
        sendBeacon();
      }
    }, 10000);

    // Fire on page hide (covers tab close, navigation away, mobile tab switch)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendBeacon();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', sendBeacon);

    return () => {
      // Send beacon for the page we're leaving (SPA navigation)
      sendBeacon();
      clearInterval(intervalId);
      window.removeEventListener('scroll', trackScroll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', sendBeacon);
    };
  }, [location.pathname]);
}
